import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { ApiResponse, PaginatedData, Space, SpaceRole } from '@/types';

interface SpaceWithCounts {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  member_count: number;
  page_count: number;
  user_role: SpaceRole | null;
}

interface SpaceMember {
  id: number;
  space_id: number;
  user_id: number;
  role: 'admin' | 'member' | 'viewer';
  created_at: string;
  user: {
    id: number;
    username: string;
    email: string;
    role: string;
    avatar_url: string | null;
  };
}

// List spaces
export function useSpaces(page: number = 1, perPage: number = 20) {
  return useQuery({
    queryKey: ['spaces', page, perPage],
    queryFn: async (): Promise<PaginatedData<SpaceWithCounts>> => {
      const response = await api.get<ApiResponse<PaginatedData<SpaceWithCounts>>>(
        `/spaces?page=${page}&perPage=${perPage}`
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch spaces');
      }
      return response.data.data;
    },
  });
}

// Get space by slug
export function useSpace(slug: string) {
  return useQuery({
    queryKey: ['space', slug],
    queryFn: async (): Promise<SpaceWithCounts> => {
      const response = await api.get<ApiResponse<SpaceWithCounts>>(`/spaces/${slug}`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch space');
      }
      return response.data.data;
    },
    enabled: !!slug,
  });
}

// Get space members
export function useSpaceMembers(slug: string) {
  return useQuery({
    queryKey: ['spaceMembers', slug],
    queryFn: async (): Promise<SpaceMember[]> => {
      const response = await api.get<ApiResponse<SpaceMember[]>>(`/spaces/${slug}/members`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch members');
      }
      return response.data.data;
    },
    enabled: !!slug,
  });
}

// Create space
export function useCreateSpace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const response = await api.post<ApiResponse<Space>>('/spaces', data);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to create space');
      }
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });
}

// Update space
export function useUpdateSpace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ slug, data }: { slug: string; data: { name?: string; description?: string } }) => {
      const response = await api.patch<ApiResponse<Space>>(`/spaces/${slug}`, data);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to update space');
      }
      return response.data.data;
    },
    onSuccess: (_, { slug }) => {
      queryClient.invalidateQueries({ queryKey: ['space', slug] });
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });
}

// Delete space
export function useDeleteSpace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string) => {
      const response = await api.delete<ApiResponse>(`/spaces/${slug}`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to delete space');
      }
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });
}

// Add member
export function useAddMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ slug, userId, role }: { slug: string; userId: number; role: 'admin' | 'member' | 'viewer' }) => {
      const response = await api.post<ApiResponse<SpaceMember>>(`/spaces/${slug}/members`, {
        user_id: userId,
        role,
      });
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to add member');
      }
      return response.data.data;
    },
    onSuccess: (_, { slug }) => {
      queryClient.invalidateQueries({ queryKey: ['spaceMembers', slug] });
    },
  });
}

// Remove member
export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ slug, userId }: { slug: string; userId: number }) => {
      const response = await api.delete<ApiResponse>(`/spaces/${slug}/members/${userId}`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to remove member');
      }
      return response.data.data;
    },
    onSuccess: (_, { slug }) => {
      queryClient.invalidateQueries({ queryKey: ['spaceMembers', slug] });
    },
  });
}

// Update member role
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ slug, userId, role }: { slug: string; userId: number; role: 'admin' | 'member' | 'viewer' }) => {
      const response = await api.patch<ApiResponse<SpaceMember>>(`/spaces/${slug}/members/${userId}/role`, { role });
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to update role');
      }
      return response.data.data;
    },
    onSuccess: (_, { slug }) => {
      queryClient.invalidateQueries({ queryKey: ['spaceMembers', slug] });
    },
  });
}

// Space page type (lighter than PageListItem)
interface SpacePage {
  id: number;
  slug: string;
  title: string;
  visibility: string;
  created_at: string;
  updated_at: string;
  author_id?: number;
  author_username?: string;
}

// List pages in a space
export function useSpacePages(slug: string, page: number = 1, perPage: number = 20) {
  return useQuery({
    queryKey: ['spacePages', slug, page, perPage],
    queryFn: async (): Promise<PaginatedData<SpacePage>> => {
      const response = await api.get<ApiResponse<PaginatedData<SpacePage>>>(
        `/spaces/${slug}/pages?page=${page}&perPage=${perPage}`
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch space pages');
      }
      return response.data.data;
    },
    enabled: !!slug,
  });
}
